import {
  AppBar,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  List,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SettingsIcon from '@mui/icons-material/Settings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LogoutIcon from '@mui/icons-material/Logout';
import InventoryIcon from '@mui/icons-material/Inventory';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useTheme } from '@mui/material/styles';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserDetails } from '../../contexts/UserDetailsContext';
import { Auth, DataStore } from 'aws-amplify';
import { useAlert, useCurrentUser, useDatastore, useIsOnline, useQmFeatureFlags } from '../../hooks';
import RaiseQMForm, { RaiseQMModel } from '../modals/RaiseQMForm';
import RaiseQMNewForm, { RaiseQMNewModel } from '../modals/RaiseQMNewForm';
import LoadingModal from '../modals/LoadingModal';
import { CreateQMProps } from '../../api/postQM';
import { createQMCoupa } from '../../common/helperFunctions/coupa';
import { printQm } from '../../common/helperFunctions/print';
import {
  confirmAttachmentRequest,
  createQmRecordRequest,
  getUploadUrlRequest,
  listQmReferenceLocationsRequest,
  updateQmRecordRequest,
  uploadFileToS3
} from '../../api/qmCrud';
import { QMPrintProps } from '../../../../Common/Types/types';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import WifiIcon from '@mui/icons-material/Wifi';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import PersonIcon from '@mui/icons-material/Person';
import { useDirtyContext } from '../../hooks/useDirtyProvider';
import { ConfirmationDialog } from '../modals/ConfirmDialog';
import { useDisabledPrinterContext } from '../../hooks/useIsDisabledPrinter';
import MenuIcon from '@mui/icons-material/Menu';
import Drawer from '@mui/material/Drawer';
import logo from '../../assets/images/headerlogo.png';
import { navbarListItems, navbarListItemsIconEnum } from '../navbar/navbardata';
import NavbarListItem from '../navbar/NavbarListItem';
import { ReactComponent as Logo } from '../../assets/images/mrllogo.svg';
import { galaxyNoteThreeDimensions } from '../../common/strings';
import RaiseQMButton, { QM_NEW_BADGE_SX } from '../buttons/RaiseQMButton';
import VersionEnvironment from './VersionEnvironment';
import PendingManifestsIndicator from './PendingManifestsIndicator';
import InfoAccordion from './InfoAccordion';
import FullSyncIndicator from './FullSyncIndicator';
import { QM_MAX_ATTACHMENTS, QM_MAX_ATTACHMENT_SIZE_BYTES } from '../../common/constants';
import { compressImageForUpload } from '../../common/imageCompression';
import { getNormalizedUserEmail } from '../../common/userIdentity';
import { toQmListRoleValue } from '../../common/qmRoles';
import { captureError } from '../../common/sentry';

const TAB_PATH_ORDER = [
  '/receipt-purchase-orders',
  '/manage-ssccs',
  '/search-reprint',
  '/receive-manifest',
  '/qm-cases',
  '/admin-panel',
  '/settings'
] as const;

const QM_NEW_BADGE_DISMISSED_KEY_PREFIX = 'ftl.qmNewBadgeDismissed';

function getTabIndexFromPath(pathname: string): number {
  if (pathname === '/' || pathname.includes('/receipt-purchase-orders')) return 0;

  const matchedIndex = TAB_PATH_ORDER.findIndex((path) => pathname.includes(path));
  return matchedIndex >= 0 ? matchedIndex : TAB_PATH_ORDER.length - 1;
}

// interface LinkTabProps {
//   label: string;
//   onClick: () => void;
// }

export default function Header() {
  const { pathname } = useLocation();
  const theme = useTheme();
  const alert = useAlert();
  const { userDetails } = useUserDetails();
  const { isOnline } = useIsOnline();
  const { qmNewDashboardEnabled: isQmNewDashboardEnabled, isLoading: isQmFeatureFlagsLoading } = useQmFeatureFlags();
  const user = useCurrentUser();
  const isMobile = useMediaQuery(galaxyNoteThreeDimensions);
  const { isDisabled: isAnyPrinterDisabled } = useDisabledPrinterContext();
  const store = useDatastore();
  const navigate = useNavigate();
  const { isDirty, setIsDirty } = useDirtyContext();
  const [pendingTab, setPendingTab] = useState<number>(0);
  const [pendingPath, setPendingPath] = useState<string>('');
  const [openRaiseQM, setOpenRaiseQM] = useState(false);
  const [openRaiseQMNew, setOpenRaiseQMNew] = useState(false);
  const [openDirtyDialog, setOpenDirtyDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const qmListRole = toQmListRoleValue(userDetails?.qmListRole);
  const hasQmCoordinatorAccess =
    !isQmFeatureFlagsLoading &&
    isQmNewDashboardEnabled &&
    ['QM_LIST_EDIT_ALL', 'QM_LIST_READ_ALL', 'QM_LIST_OWN_ONLY'].includes(qmListRole);
  const normalizedUserEmail = getNormalizedUserEmail(user.email);
  const qmNewBadgeDismissedStorageKey = useMemo(() => {
    const userKey = String(normalizedUserEmail || user?.cognitoUser?.username || 'unknown')
      .trim()
      .toLowerCase();
    return `${QM_NEW_BADGE_DISMISSED_KEY_PREFIX}.${userKey}`;
  }, [normalizedUserEmail, user?.cognitoUser?.username]);
  const [isQmNewBadgeDismissed, setIsQmNewBadgeDismissed] = useState(false);
  const [tab, setTab] = useState<number>(getTabIndexFromPath(pathname));
  const isMountedRef = useRef(true);

  const safeAlert = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
    if (!isMountedRef.current) return;
    alert(message, severity);
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    try {
      const dismissed = window.localStorage.getItem(qmNewBadgeDismissedStorageKey) === 'true';
      setIsQmNewBadgeDismissed(dismissed);
    } catch {
      setIsQmNewBadgeDismissed(false);
    }
  }, [qmNewBadgeDismissedStorageKey]);

  const dismissQmNewBadge = () => {
    setIsQmNewBadgeDismissed(true);
    try {
      window.localStorage.setItem(qmNewBadgeDismissedStorageKey, 'true');
    } catch {
      // Ignore storage write issues; this is best-effort UX state.
    }
  };

  const openQmNewFlow = () => {
    if (isAnyPrinterDisabled) {
      return;
    }
    dismissQmNewBadge();
    setOpenRaiseQMNew(true);
  };

  // const StyledTabs = styled(Tabs)(({ theme }) => ({
  //   '& .MuiTabs-indicator': {
  //     top: '-8px',
  //     height: '0.4em',
  //     backgroundColor: theme.palette.secondary.light,
  //     marginTop: 0,
  //     transform: 'translateY(0)'
  //   },
  //   '& .MuiTabs-root': {
  //     minHeight: 'unset',
  //     paddingTop: 0
  //   },
  //   '& .MuiTab-root': {
  //     minHeight: 'unset',
  //     paddingTop: 0,
  //     paddingBottom: 0,
  //     marginTop: 0
  //   },
  //   minHeight: 'unset',
  //   paddingTop: 0
  // }));

  // const LinkTab = styled(({ label, onClick, ...otherProps }: LinkTabProps) => (
  //   <Tab
  //     {...otherProps}
  //     label={label}
  //     onClick={(event) => {
  //       event.preventDefault();
  //       onClick();
  //     }}
  //   />
  // ))(({ theme }) => ({
  //   fontSize: '14px',
  //   color: theme.palette.primary.contrastText,
  //   marginRight: '1em',
  //   '&.Mui-selected': {
  //     color: theme.palette.primary.contrastText,
  //     fontWeight: theme.typography.fontWeightBold
  //   }
  // }));

  async function onQMSubmit(data: RaiseQMModel) {
    try {
      setIsLoading(true);

      const statuses = (await store.workflowStatuses.getArray())?.filter((s) => s.name === 'Created');
      if (!statuses || !statuses[0].workflowStatusId) {
        alert('Error getting workflow status, please try again.', 'warning');
        return;
      }
      const createdId = statuses[0].workflowStatusId;
      const userPrinter = await store.printers.getPrinterForUser(user.cognitoUser.username);

      if (!userPrinter) {
        alert('Error getting printer, please set your printer and try again', 'error');
        return;
      }

      const createQMPayload: CreateQMProps = {
        workflowStatusId: createdId,
        reasonId: data.reason.reasonId,
        issueId: data.issue.issueId,
        issue: data.issue.name,
        reason: data.reason.name,
        locationName: data.locationName,
        description: data.description,
        purchaseOrderNumber: data.purchaseOrderNumber,
        progress: data.progress,
        // attachments: data.attachments, //@Phase3: Add attachments
        printerIP: userPrinter?.printerIP,
        port: userPrinter?.printerPort,
        numberOfLabels: data.numberOfLabels,
        createdBy: normalizedUserEmail || user.name
      };

      const response = await createQMCoupa(createQMPayload);

      if (response.error) {
        alert('Error creating QM', 'error');
        return;
      }

      alert('QM created', 'success');
    } catch (error) {
      alert(`An error occurred when submitting QM: ${error}`, 'error');
      console.error('QM submission error: ', error);
      captureError(error, 'header-onQMSubmit');
    } finally {
      setIsLoading(false);
      setOpenRaiseQM(false);
    }
  }

  async function onQMNewSubmit(data: RaiseQMNewModel) {
    try {
      setIsLoading(true);

      const reasonCode = String(data.reason.reasonId || data.reason.id || '').trim();

      if (!reasonCode) {
        alert('Reason code is required.', 'warning');
        return;
      }

      const attachments = data.attachments || [];
      if (attachments.length > QM_MAX_ATTACHMENTS) {
        alert(`A maximum of ${QM_MAX_ATTACHMENTS} attachments is allowed.`, 'warning');
        return;
      }

      const oversizedAttachment = attachments.find((file) => file.size > QM_MAX_ATTACHMENT_SIZE_BYTES);
      if (oversizedAttachment) {
        const maxSizeInMb = Math.round((QM_MAX_ATTACHMENT_SIZE_BYTES / (1024 * 1024)) * 10) / 10;
        alert(`Attachment "${oversizedAttachment.name}" exceeds the ${maxSizeInMb}MB limit.`, 'warning');
        return;
      }

      const lookup = String(data.ssccOrAsn || '').trim();
      const upperLookup = lookup.toUpperCase();
      const isAsn = /^MRL[A-Za-z0-9]{8}$/i.test(upperLookup);
      const isSscc = /^[A-Za-z0-9]{15}$/.test(lookup);
      const parsedPackageCount = Number(data.numberOfLabels);
      const packageCount = Number.isFinite(parsedPackageCount) && parsedPackageCount > 0 ? parsedPackageCount : null;
      const normalizedPoLines = (data.poLineSelections || []).map((line) => ({
        poLineId: String(line.poLineId || '').trim() || null,
        poLine: String(line.poLine || '').trim() || null,
        itemId: String(line.itemId || '').trim() || null,
        linePayload: line.linePayload || null,
        orderedQuantity:
          typeof line.orderedQuantity === 'number' && Number.isFinite(line.orderedQuantity)
            ? line.orderedQuantity
            : null,
        receivedQuantity:
          typeof line.receivedQuantity === 'number' && Number.isFinite(line.receivedQuantity)
            ? line.receivedQuantity
            : null,
        quantityVariance:
          typeof line.quantityVariance === 'number' && Number.isFinite(line.quantityVariance)
            ? line.quantityVariance
            : null
      }));
      const normalizedPackageConfigurations = (data.packageConfigurations || [])
        .map((pkg) => ({
          packageNumber: Number(pkg.packageNumber),
          packageType: String(pkg.packageType || '').trim() || null
        }))
        .filter((pkg) => Number.isInteger(pkg.packageNumber) && pkg.packageNumber > 0);

      const firstSelectedPoLine = normalizedPoLines[0] || null;
      const firstPackageConfiguration = normalizedPackageConfigurations[0] || null;

      const created = await createQmRecordRequest({
        type: data.type,
        reasonCode,
        sscc: isSscc ? lookup : null,
        asn: isAsn ? upperLookup : null,
        poNumber: String(data.purchaseOrderNumber || '').trim() || null,
        poLine: firstSelectedPoLine?.poLine || String(data.purchaseOrderLine || '').trim() || null,
        poLineId: firstSelectedPoLine?.poLineId || String(data.poLineId || '').trim() || null,
        itemId: firstSelectedPoLine?.itemId || String(data.itemId || '').trim() || null,
        supplierCode: String(data.supplierCode || '').trim() || null,
        supplierName: String(data.supplierName || '').trim() || null,
        contactName: String(data.contactName || '').trim() || null,
        itemLocation: data.locationName,
        packageCount:
          normalizedPackageConfigurations.length > 0 ? normalizedPackageConfigurations.length : packageCount,
        packageType: firstPackageConfiguration?.packageType || String(data.packageTypes || '').trim() || null,
        otherInformation: String(data.other || '').trim() || null,
        description: String(data.description || '').trim() || null,
        poLines: normalizedPoLines.length > 0 ? normalizedPoLines : null,
        packages: normalizedPackageConfigurations.length > 0 ? normalizedPackageConfigurations : null,
        createdBy: normalizedUserEmail
      });

      const closureCode = String(data.closureCode || '').trim() || null;

      if (closureCode) {
        await updateQmRecordRequest({
          qmId: created.qmId,
          status: 'CLOSED',
          closureCode,
          closureNotes: data.closureNotes || null,
          updatedBy: normalizedUserEmail,
          note: 'Closed at create time from Raise a QM (New)'
        });
      }

      if (attachments.length > 0) {
        const uploadResults = await Promise.allSettled(
          attachments.map(async (originalFile) => {
            const file = await compressImageForUpload(originalFile);

            const { uploadUrl, s3Key } = await getUploadUrlRequest({
              qmId: created.qmId,
              fileName: file.name,
              mimeType: file.type || null
            });

            await uploadFileToS3(uploadUrl, file);

            await confirmAttachmentRequest({
              qmId: created.qmId,
              s3Key,
              fileName: file.name,
              mimeType: file.type || null,
              sizeBytes: file.size,
              uploadedBy: normalizedUserEmail
            });

            return file.name;
          })
        );

        const failedUploads: string[] = [];

        uploadResults.forEach((result, index) => {
          if (result.status === 'fulfilled') return;

          const fileName = attachments[index]?.name || 'unknown-file';
          failedUploads.push(fileName);
          console.error(`Raise QM attachment upload failed for ${fileName}:`, result.reason);
          captureError(result.reason, 'header-onQMNewSubmit-attachment-upload', {
            extra: { fileName, qmId: created.qmId, qmNumber: created.qmNumber }
          });
        });

        if (failedUploads.length > 0) {
          safeAlert(
            `QM ${created.qmNumber} created, but ${failedUploads.length} attachment(s) failed to upload (${failedUploads.join(', ')}).`,
            'warning'
          );
        }
      }

      const effectivePackageCount = Number(created.packageCount || packageCount || 0);

      if (effectivePackageCount > 0) {
        void (async () => {
          try {
            const userPrinter = await store.printers.getPrinterForUser(user.cognitoUser.username);

            if (!userPrinter) {
              safeAlert(
                `QM ${created.qmNumber} created, but no printer is set for your account. Labels were not printed.`,
                'warning'
              );
              return;
            }

            const createdAt = new Date(created.createdAt);
            const formattedCreatedAt = Number.isNaN(createdAt.getTime())
              ? created.createdAt
              : createdAt.toLocaleDateString('en-AU');
            const reasonForLabel = String(created.reasonLabel || data.reason?.name || '').trim();
            const issueForLabel = '-';
            const labelDescription = String(created.description || '').trim();
            const rawLocationCode = String(created.itemLocation || data.locationName || '').trim();
            let locationNameForLabel = rawLocationCode;

            if (rawLocationCode) {
              try {
                const locationOptions = await listQmReferenceLocationsRequest();
                const normalizedLocationCode = rawLocationCode.toLowerCase();
                const matchedLocation = locationOptions.find(
                  (location) =>
                    String(location.code || '')
                      .trim()
                      .toLowerCase() === normalizedLocationCode ||
                    String(location.label || '')
                      .trim()
                      .toLowerCase() === normalizedLocationCode
                );

                if (matchedLocation?.label) {
                  locationNameForLabel = matchedLocation.label;
                }
              } catch {
                locationNameForLabel = rawLocationCode;
              }
            }

            const qmPrintEvents: QMPrintProps[] = Array.from({ length: effectivePackageCount }, (_, index) => ({
              createdAt: formattedCreatedAt,
              createdBy: String(normalizedUserEmail || user.name || '').trim(),
              packageNumber: String(index + 1),
              totalPackages: String(effectivePackageCount),
              qmNumber: String(created.qmNumber || ''),
              poNumber: String(created.poNumber || ''),
              reason: reasonForLabel,
              issue: issueForLabel,
              locationName: locationNameForLabel,
              description: labelDescription
            }));

            for (const printEvent of qmPrintEvents) {
              const printResponse = await printQm([printEvent], userPrinter.printerIP, userPrinter.printerPort);
              if (printResponse.error) {
                safeAlert(
                  `QM ${created.qmNumber} created, but label ${printEvent.packageNumber} printing failed.`,
                  'warning'
                );
                return;
              }
            }

            safeAlert(`QM created: ${created.qmNumber}`, 'success');
          } catch (error) {
            safeAlert(`QM ${created.qmNumber} created, but background label printing failed.`, 'warning');
            console.error('Background QM printing error: ', error);
            captureError(error, 'header-onQMNewSubmit-background-print', {
              extra: { qmId: created.qmId, qmNumber: created.qmNumber }
            });
          }
        })();
      } else {
        safeAlert(`QM created: ${created.qmNumber}`, 'success');
      }
    } catch (error) {
      alert(`An error occurred when creating new QM: ${error}`, 'error');
      console.error('QM new submission error: ', error);
      captureError(error, 'header-onQMNewSubmit');
    } finally {
      setIsLoading(false);
      setOpenRaiseQMNew(false);
    }
  }

  function handleChangeTab(newValue: number, path: string) {
    if (isDirty) {
      setPendingPath(path);
      setPendingTab(newValue);
      setOpenDirtyDialog(true);
    } else {
      setTab(newValue);
      navigate(path);
    }
  }

  function handleDialogConfirm() {
    setOpenDirtyDialog(false);
    setTab(pendingTab);
    navigate(pendingPath);
    setIsDirty(false);
  }

  function getIcon(icon: any) {
    switch (icon) {
      case navbarListItemsIconEnum.RECEIPT:
        return <ReceiptIcon />;
      case navbarListItemsIconEnum.SSCC:
        return <InventoryIcon />;
      case navbarListItemsIconEnum.REPRINT:
        return <PrintIcon />;
      case navbarListItemsIconEnum.MANIFEST:
        return <LocalShippingIcon />;
      case navbarListItemsIconEnum.QM_COORDINATOR:
        return <DashboardIcon />;
      case navbarListItemsIconEnum.ADMIN_PANEL:
        return <AdminPanelSettingsIcon />;
      case navbarListItemsIconEnum.SETTINGS:
        return <SettingsIcon />;
      case navbarListItemsIconEnum.LOG_OUT:
        return <LogoutIcon />;
    }
  }

  function logoutUser() {
    DataStore.stop().then(() => DataStore.clear());
    Auth.signOut();
  }

  return (
    <>
      <AppBar
        elevation={0}
        sx={{
          height: '4em',
          background: 'linear-gradient(135deg, #000000 0%, #424242 100%) !important',
          boxShadow: 'none',
          '& .MuiToolbar-root': { paddingTop: 0, paddingBottom: 0, minHeight: '4em' }
        }}>
        {isMobile ? (
          <>
            <Container maxWidth={false} disableGutters sx={{ px: 1 }}>
              <Toolbar
                disableGutters
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 0.5,
                  minHeight: '4em'
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <IconButton size="large" aria-label="open drawer" onClick={() => setDrawerOpen(true)} color="inherit">
                    <MenuIcon />
                  </IconButton>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center', px: 0.5 }}>
                  <img
                    src={logo}
                    alt="Logo"
                    height="32"
                    style={{ paddingTop: '0.30em', paddingLeft: 0, maxWidth: '100%', objectFit: 'contain' }}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexShrink: 0,
                    '& .MuiButton-root': {
                      minWidth: 'auto',
                      px: 1,
                      fontSize: '0.75rem'
                    }
                  }}>
                  <FullSyncIndicator compact />
                  {isQmFeatureFlagsLoading ? null : !isQmNewDashboardEnabled ? (
                    <RaiseQMButton
                      isOnline={isOnline}
                      isAnyPrinterDisabled={isAnyPrinterDisabled}
                      setOpenRaiseQM={setOpenRaiseQM}
                    />
                  ) : (
                    <RaiseQMButton
                      isOnline={isOnline}
                      isAnyPrinterDisabled={isAnyPrinterDisabled}
                      setOpenRaiseQM={setOpenRaiseQM}
                      showNewBadge={!isQmNewBadgeDismissed}
                      onClick={openQmNewFlow}
                    />
                  )}
                </Box>
              </Toolbar>
            </Container>
            <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
              <Box
                sx={{
                  width: 250,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%'
                }}
                role="presentation"
                onClick={() => setDrawerOpen(false)}
                onKeyDown={() => setDrawerOpen(false)}>
                <Box sx={{ overflow: 'auto' }}>
                  <List>
                    <Logo width="90%" height="50%" />
                    <Divider />
                    {navbarListItems
                      .filter((item) => {
                        if (item.path === '/qm-cases') {
                          return hasQmCoordinatorAccess;
                        }
                        // Hide admin panel if user does not have access
                        if (item.path === '/admin-panel') {
                          return !!userDetails?.adminPanelAccess;
                        }
                        return true;
                      })
                      .map((item, index) => (
                        <React.Fragment key={index}>
                          <NavbarListItem
                            text={item.text}
                            icon={getIcon(item.icon)}
                            handleSelect={() => {
                              item.path === '/logout' ? logoutUser() : navigate(item.path);
                            }}
                          />
                          <Divider sx={{ m: 0, p: 0 }} />
                        </React.Fragment>
                      ))}
                  </List>
                </Box>
                <Box sx={{ padding: '1em', mt: 'auto' }}>
                  <Typography variant="body2" color={theme.palette.text.primary}>
                    {isOnline ? (
                      ''
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 2 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                          Offline
                        </Typography>
                        <Tooltip
                          enterTouchDelay={0}
                          title={
                            <Box sx={{ p: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: 'white' }}>
                                Offline
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'rgba(255, 255, 255, 0.9)',
                                  lineHeight: 1.4
                                }}>
                                You're currently disconnected from the internet. Any changes you make will be saved
                                locally and automatically synced when your connection is restored.
                              </Typography>
                            </Box>
                          }
                          arrow
                          placement="top"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: 'rgba(0, 0, 0, 0.9)',
                                maxWidth: 280,
                                borderRadius: 2,
                                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                                border: '1px solid rgba(255, 255, 255, 0.1)'
                              }
                            },
                            arrow: {
                              sx: {
                                color: 'rgba(0, 0, 0, 0.9)'
                              }
                            }
                          }}>
                          <Box
                            sx={{
                              animation: 'fade 3s infinite',
                              '@keyframes fade': {
                                '0%': { opacity: 1 },
                                '50%': { opacity: 0.3 },
                                '100%': { opacity: 1 }
                              }
                            }}>
                            <WifiOffIcon color="info" />
                          </Box>
                        </Tooltip>
                      </Box>
                    )}
                  </Typography>

                  {/* Mobile Pending Manifests Indicator */}
                  <Box sx={{ mt: 1 }}>
                    <PendingManifestsIndicator />
                  </Box>
                  <Stack>
                    <Box>
                      <Typography variant="body2" color={theme.palette.text.primary}>
                        {process.env.REACT_APP_ENV === 'prod' ? '' : <VersionEnvironment />}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        component="div"
                        variant="body2"
                        color={theme.palette.text.primary}
                        sx={{ maxWidth: '100%', wordWrap: 'break-word' }}>
                        {isOnline ? `${user.name} (${user.role})` : ``}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Box>
            </Drawer>
          </>
        ) : (
          <Container maxWidth={false} sx={{ px: 2, height: '100%', py: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: '100%',
                width: '100%'
              }}>
              {/* Left Section - Logo and Navigation */}
              <Stack direction="row" alignItems="center" spacing={3}>
                {/* Logo */}
                <img src={logo} alt="Logo" height="35em" style={{ paddingTop: '0.30em' }} />

                {/* Navigation Tabs - Underline Style */}
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    onClick={() => handleChangeTab(0, '/receipt-purchase-orders')}
                    sx={{
                      color: theme.palette.primary.contrastText,
                      fontSize: '14px',
                      fontWeight: tab === 0 ? 600 : 400,
                      textTransform: 'none',
                      px: 2,
                      py: 1,
                      borderBottom: tab === 0 ? `3px solid ${theme.palette.secondary.light}` : '3px solid transparent',
                      borderRadius: 0,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}>
                    Receipt Purchase Orders
                  </Button>

                  <Button
                    onClick={() => handleChangeTab(1, '/manage-ssccs')}
                    sx={{
                      color: theme.palette.primary.contrastText,
                      fontSize: '14px',
                      fontWeight: tab === 1 ? 600 : 400,
                      textTransform: 'none',
                      px: 2,
                      py: 1,
                      borderBottom: tab === 1 ? `3px solid ${theme.palette.secondary.light}` : '3px solid transparent',
                      borderRadius: 0,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}>
                    Manage SSCCs
                  </Button>

                  <Button
                    onClick={() => handleChangeTab(2, '/search-reprint')}
                    sx={{
                      color: theme.palette.primary.contrastText,
                      fontSize: '14px',
                      fontWeight: tab === 2 ? 600 : 400,
                      textTransform: 'none',
                      px: 2,
                      py: 1,
                      borderBottom: tab === 2 ? `3px solid ${theme.palette.secondary.light}` : '3px solid transparent',
                      borderRadius: 0,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}>
                    Search and Reprint
                  </Button>

                  <Button
                    onClick={() => handleChangeTab(3, '/receive-manifest')}
                    sx={{
                      color: theme.palette.primary.contrastText,
                      fontSize: '14px',
                      fontWeight: tab === 3 ? 600 : 400,
                      textTransform: 'none',
                      px: 2,
                      py: 1,
                      borderBottom: tab === 3 ? `3px solid ${theme.palette.secondary.light}` : '3px solid transparent',
                      borderRadius: 0,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}>
                    Receive Manifest
                  </Button>

                  {hasQmCoordinatorAccess && (
                    <Button
                      onClick={() => handleChangeTab(4, '/qm-cases')}
                      sx={{
                        color: theme.palette.primary.contrastText,
                        fontSize: '14px',
                        fontWeight: tab === 4 ? 600 : 400,
                        textTransform: 'none',
                        px: 2,
                        py: 1,
                        borderBottom:
                          tab === 4 ? `3px solid ${theme.palette.secondary.light}` : '3px solid transparent',
                        borderRadius: 0,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}>
                      QM Cases
                    </Button>
                  )}

                  {!!userDetails?.adminPanelAccess && (
                    <Button
                      onClick={() => handleChangeTab(5, '/admin-panel')}
                      sx={{
                        color: theme.palette.primary.contrastText,
                        fontSize: '14px',
                        fontWeight: tab === 5 ? 600 : 400,
                        textTransform: 'none',
                        px: 2,
                        py: 1,
                        borderBottom:
                          tab === 5 ? `3px solid ${theme.palette.secondary.light}` : '3px solid transparent',
                        borderRadius: 0,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}>
                      Admin Panel
                    </Button>
                  )}

                  <Button
                    onClick={() => handleChangeTab(6, '/settings')}
                    sx={{
                      color: theme.palette.primary.contrastText,
                      fontSize: '14px',
                      fontWeight: tab === 6 ? 600 : 400,
                      textTransform: 'none',
                      px: 2,
                      py: 1,
                      borderBottom: tab === 6 ? `3px solid ${theme.palette.secondary.light}` : '3px solid transparent',
                      borderRadius: 0,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}>
                    Settings
                  </Button>
                </Stack>
              </Stack>

              {/* Right Section */}
              <Stack direction="column" alignItems="end">
                {/* Top Row - Buttons */}
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                  {/* Info Accordion */}
                  <InfoAccordion />

                  {/* Connection Status Indicator */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 0.5,
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      cursor: 'default',
                      transition: 'all 0.2s ease-in-out'
                    }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        color: theme.palette.primary.contrastText
                      }}>
                      {isOnline ? 'Online' : 'Offline'}
                    </Typography>
                    <Tooltip
                      enterTouchDelay={0}
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: 'white' }}>
                            {isOnline ? 'Online' : 'Offline'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.4 }}>
                            {isOnline
                              ? 'You have a stable internet connection. All features are available.'
                              : "You're currently disconnected from the internet. Please reconnect to continue using FTL."}
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="bottom"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: 'rgba(0, 0, 0, 0.9)',
                            maxWidth: 280,
                            borderRadius: 2,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                          }
                        },
                        arrow: {
                          sx: {
                            color: 'rgba(0, 0, 0, 0.9)'
                          }
                        }
                      }}>
                      <Box
                        sx={{
                          animation: isOnline ? 'none' : 'pulse 2s infinite',
                          '@keyframes pulse': {
                            '0%': { opacity: 1 },
                            '50%': { opacity: 0.3 },
                            '100%': { opacity: 1 }
                          }
                        }}>
                        {isOnline ? (
                          <WifiIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                        ) : (
                          <WifiOffIcon sx={{ fontSize: 16, color: '#f44336' }} />
                        )}
                      </Box>
                    </Tooltip>
                  </Box>

                  <FullSyncIndicator />

                  {/* Pending Manifests Indicator */}
                  <PendingManifestsIndicator />

                  {/* Raise QM New Button */}
                  {isQmFeatureFlagsLoading ? null : isQmNewDashboardEnabled ? (
                    isOnline ? (
                      <Tooltip
                        enterTouchDelay={0}
                        title={
                          isAnyPrinterDisabled
                            ? 'A Printer in your settings is disabled, some functionality is restricted. Please select a new printer in Settings.'
                            : 'This launches the new in-progress QM popup flow.'
                        }>
                        <Button
                          onClick={openQmNewFlow}
                          startIcon={<ReportProblemIcon sx={{ fontSize: 16 }} />}
                          sx={{
                            color: theme.palette.primary.contrastText,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            textTransform: 'none',
                            px: 2,
                            py: 0.75,
                            borderRadius: 2,
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              borderColor: 'rgba(255, 255, 255, 0.3)'
                            },
                            transition: 'all 0.2s ease-in-out'
                          }}>
                          {isQmNewBadgeDismissed ? (
                            'Raise a QM'
                          ) : (
                            <Badge
                              badgeContent="new"
                              color="default"
                              overlap="rectangular"
                              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                              sx={QM_NEW_BADGE_SX}>
                              <span>Raise a QM</span>
                            </Badge>
                          )}
                        </Button>
                      </Tooltip>
                    ) : (
                      <Tooltip
                        enterTouchDelay={0}
                        title={
                          <Box sx={{ p: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: 'white' }}>
                              Feature Unavailable Offline
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.4 }}>
                              Raising a QM requires internet connectivity for real-time integration with external
                              systems and printing services.
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="bottom"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: 'rgba(0, 0, 0, 0.9)',
                              maxWidth: 280,
                              borderRadius: 2,
                              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                              border: '1px solid rgba(255, 255, 255, 0.1)'
                            }
                          },
                          arrow: {
                            sx: {
                              color: 'rgba(0, 0, 0, 0.9)'
                            }
                          }
                        }}>
                        <span>
                          <Button
                            disabled
                            startIcon={<ReportProblemIcon sx={{ fontSize: 16 }} />}
                            sx={{
                              color: theme.palette.primary.contrastText,
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              textTransform: 'none',
                              px: 2,
                              py: 0.75,
                              borderRadius: 2,
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              opacity: 0.5,
                              '&.Mui-disabled': {
                                color: theme.palette.primary.contrastText,
                                borderColor: 'rgba(255, 255, 255, 0.2)'
                              }
                            }}>
                            {isQmNewBadgeDismissed ? (
                              'Raise a QM'
                            ) : (
                              <Badge
                                badgeContent="new"
                                color="default"
                                overlap="rectangular"
                                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                sx={QM_NEW_BADGE_SX}>
                                <span>Raise a QM</span>
                              </Badge>
                            )}
                          </Button>
                        </span>
                      </Tooltip>
                    )
                  ) : isOnline ? (
                    <Tooltip
                      enterTouchDelay={0}
                      title={
                        isAnyPrinterDisabled
                          ? 'A Printer in your settings is disabled, some functionality is restricted. Please select a new printer in Settings.'
                          : ''
                      }>
                      <Button
                        onClick={() => setOpenRaiseQM(!isAnyPrinterDisabled)}
                        startIcon={<ReportProblemIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          color: theme.palette.primary.contrastText,
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          textTransform: 'none',
                          px: 2,
                          py: 0.75,
                          borderRadius: 2,
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            borderColor: 'rgba(255, 255, 255, 0.3)'
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}>
                        Raise a QM
                      </Button>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      enterTouchDelay={0}
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: 'white' }}>
                            Feature Unavailable Offline
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.4 }}>
                            Raising a QM requires internet connectivity for real-time integration with external systems
                            and printing services.
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="bottom"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: 'rgba(0, 0, 0, 0.9)',
                            maxWidth: 280,
                            borderRadius: 2,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                          }
                        },
                        arrow: {
                          sx: {
                            color: 'rgba(0, 0, 0, 0.9)'
                          }
                        }
                      }}>
                      <span>
                        <Button
                          disabled
                          startIcon={<ReportProblemIcon sx={{ fontSize: 16 }} />}
                          sx={{
                            color: theme.palette.primary.contrastText,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            textTransform: 'none',
                            px: 2,
                            py: 0.75,
                            borderRadius: 2,
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            opacity: 0.5,
                            '&.Mui-disabled': {
                              color: theme.palette.primary.contrastText,
                              borderColor: 'rgba(255, 255, 255, 0.2)'
                            }
                          }}>
                          Raise a QM
                        </Button>
                      </span>
                    </Tooltip>
                  )}

                  {/* Logout Button */}
                  <Button
                    onClick={logoutUser}
                    startIcon={<LogoutIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      color: theme.palette.primary.contrastText,
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      textTransform: 'none',
                      px: 2,
                      py: 0.75,
                      borderRadius: 2,
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderColor: 'rgba(255, 255, 255, 0.3)'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}>
                    Logout
                  </Button>

                  {/* User Info Icon - Farthest Right */}
                  <Tooltip
                    enterTouchDelay={0}
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: 'white' }}>
                          System Information
                        </Typography>
                        {process.env.REACT_APP_ENV !== 'prod' && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'rgba(255, 255, 255, 0.9)',
                              lineHeight: 1.4,
                              mb: 0.5
                            }}>
                            <strong>Environment:</strong> <VersionEnvironment />
                          </Typography>
                        )}
                        {isOnline && (
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.4 }}>
                            <strong>Logged in as:</strong> {user.name} ({user.role})
                          </Typography>
                        )}
                      </Box>
                    }
                    arrow
                    placement="bottom"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: 'rgba(0, 0, 0, 0.9)',
                          maxWidth: 280,
                          borderRadius: 2,
                          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }
                      },
                      arrow: {
                        sx: {
                          color: 'rgba(0, 0, 0, 0.9)'
                        }
                      }
                    }}>
                    <IconButton
                      sx={{
                        color: theme.palette.primary.contrastText,
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          borderColor: 'rgba(255, 255, 255, 0.3)'
                        },
                        transition: 'all 0.2s ease-in-out',
                        width: 36,
                        height: 36
                      }}>
                      <PersonIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Box>
          </Container>
        )}
      </AppBar>
      {openRaiseQM && <RaiseQMForm open={openRaiseQM} onClose={() => setOpenRaiseQM(false)} onSubmit={onQMSubmit} />}
      {openRaiseQMNew && (
        <RaiseQMNewForm open={openRaiseQMNew} onClose={() => setOpenRaiseQMNew(false)} onSubmit={onQMNewSubmit} />
      )}
      <LoadingModal
        isLoading={isLoading}
        titleText={'Creating QM and Printing'}
        bodyText={'Please wait, creating a QM and printing labels.'}
      />
      <ConfirmationDialog
        open={openDirtyDialog}
        onClose={() => setOpenDirtyDialog(false)}
        onConfirm={handleDialogConfirm}
        title="Unsaved Changes"
        content="You have unsaved changes. Are you sure you want to leave the page?"
      />
    </>
  );
}
